import { Link } from 'react-router-dom';
import { ChevronRight, MessageCircle, Plus, ShieldAlert } from 'lucide-react';
import type { Claim } from '@cover/core/api/types.ts';
import { useBrand, whatsappLink } from '@cover/core/brand.tsx';
import { CLAIM_STATUS_LABEL, date, money } from '@cover/core/format.ts';
import { Badge, type Tone } from '@cover/core/ui/Badge.tsx';
import { Button } from '@cover/core/ui/Button.tsx';
import { Card } from '@cover/core/ui/Card.tsx';
import { Empty, Load } from '@cover/core/ui/States.tsx';
import { useClaims } from '../api.ts';
import { PageTitle } from '../Layout.tsx';

export const CLAIM_TYPE: Record<Claim['type'], string> = {
  theft: 'Theft',
  damage: 'Damage',
  injury: 'Injury',
  property_damage_third_party: 'Damage to someone else’s property',
  other: 'Other',
};

export function claimTone(s: Claim['status']): Tone {
  return s === 'closed' ? 'neutral' : s === 'awaiting_info' ? 'warn' : 'info';
}

export function ClaimsPage() {
  const claims = useClaims();
  const brand = useBrand();
  const wa = whatsappLink(brand, 'I need to make a claim');
  return (
    <>
      <PageTitle lead="Report something here or in WhatsApp, then follow it and add photos or documents as you go.">Claims</PageTitle>
      <Load q={claims}>
        {(list) => (
          <div className="space-y-4">
            {list.length === 0 ? (
              <Card>
                <Empty icon={<ShieldAlert className="h-8 w-8" aria-hidden />} title="No claims">
                  If something’s happened, report it below and we’ll take it from there.
                </Empty>
              </Card>
            ) : (
              <ul className="space-y-3">
                {list.map((c) => (
                  <li key={c.id}>
                    <Link to={`/claims/${c.id}`} className="block rounded-lg border border-line bg-white px-4 py-4 text-ink no-underline hover:border-brand/40 hover:no-underline">
                      <div className="flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold">{CLAIM_TYPE[c.type]}</span>
                            <Badge tone={claimTone(c.status)}>{CLAIM_STATUS_LABEL[c.status]}</Badge>
                          </div>
                          <p className="mt-1 text-sm text-mid">
                            <span className="font-mono">{c.reference}</span> · {date(c.occurredAt)}
                            {c.claimedValuePence ? ` · ${money(c.claimedValuePence)} of items` : ''}
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted" aria-hidden />
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <Button asChild size="full">
              <Link to="/claims/new" className="text-on-brand no-underline hover:no-underline">
                <Plus className="h-5 w-5" aria-hidden /> Report something
              </Link>
            </Button>
            {wa && (
              <Button asChild variant="secondary" size="full">
                <a href={wa} className="text-ink no-underline hover:no-underline">
                  <MessageCircle className="h-5 w-5" aria-hidden /> Or tell us in WhatsApp
                </a>
              </Button>
            )}
          </div>
        )}
      </Load>
    </>
  );
}
